import * as FileSaver from 'file-saver';
import * as XLSX from 'xlsx';

import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';

import ArgonBox from 'component/Argon/ArgonBox';
import ArgonTypography from 'component/Argon/ArgonTypography';

import DashboardLayout from 'layout/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'component/Navbars/DashboardNavbar';

import DataTable from 'component/Tables/DataTable';
import StatusCell from './component/StatusCell';

import { PickerDate } from 'component/basicForm';

import { useReport } from 'api/admin/getReport';
import { useEffect, useState } from 'react';
import mongoIdToDt from 'utils/mongoIdToDt';
import ArgonButton from 'component/Argon/ArgonButton';
import { format, startOfDay, subDays, parse } from 'date-fns';
import { th } from 'date-fns/locale';
import usePkg from 'hook/usePkg';
import { Link } from 'react-router-dom';
import { useReportPolicy } from 'api/admin/getReportPolicy';
import { Checkbox, FormControlLabel } from '@mui/material';

const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
const fileExtension = '.xlsx';

const curDate = new Date();
const st_date = startOfDay(curDate);

const PKG = { '3m': 750, '6m': 990, '1y': 1790, '15m': 2475 };
const header = [
  'prefix',
  'name',
  'surename',
  'bd',
  'nationality',
  'idCard',
  'cardType',
  'policyDate',
  'employer',
  'employerId',
  'address1',
  'district',
  'area',
  'province',
  'zipcode',
  'status',
  'doctype',
  'pkg',
  'invoiceId',
  'applicationNo',
  'requestNo',
  'successAt',
  'policyNo',
  'urlPolicy',
  'urlPolicyLocal',
  'amount',
  'discount',
  'netAmount',
  'createAt',
  'qrURL',
  'isPaid',
  'email',
  'isRenew',
  'sex',
  'error',
  'employerEmail',
  'docType',
  'employerPhone',
];

const AdminReport = () => {
  const { discount: dc } = usePkg();
  const [start, setStart] = useState(subDays(st_date, 1));
  const [end, setEnd] = useState(st_date);
  const [isUser, setIsUser] = useState(true);
  const [isAdmin, setIsAdmin] = useState(true);
  const {
    data: rows,
    refetch,
    fetchStatus,
  } = useReport(format(start, 'dd/MM/yyyy', { locale: th }), format(end, 'dd/MM/yyyy', { locale: th }), isUser, isAdmin);
  const { refetch: fetchPolicy, fetchStatus: fetchPolicyStatus } = useReportPolicy(
    format(start, 'dd/MM/yyyy', { locale: th }),
    format(end, 'dd/MM/yyyy', { locale: th }),
    isUser,
    isAdmin
  );
  const [loading, setLoading] = useState(false);
  const [loadingPolicy, setLoadingPolicy] = useState(false);

  // useEffect(() => {
  //   console.log(data);
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [isFetched]);
  useEffect(() => {
    if (fetchStatus === 'fetching') {
      setLoading(true);
    } else {
      setLoading(false);
    }
  }, [fetchStatus]);

  useEffect(() => {
    if (fetchPolicyStatus === 'fetching') {
      setLoadingPolicy(true);
    } else {
      setLoadingPolicy(false);
    }
  }, [fetchPolicyStatus]);

  function handleFetch() {
    // console.log('fetch');
    setLoading(true);
    refetch()
      .then((res) => {
        console.log(res);
      })
      .catch(console.error);
  }

  async function exportOrder() {
    try {
      // console.log(res.data);
      let orders = rows;
      if (!rows.length) {
        await refetch().then((res) => {
          orders = res.data;
        });
      }
      const reports = [];
      for (let i = 0; i < orders.length; i++) {
        const order = orders[i];
        delete order?.userDiscount;
        delete order?.gbResult;
        delete order?.coupon;
        delete order?.code;
        reports.push({
          createAt: format(formatIdToDt(order._id), 'dd-MM-yyyy HH:mm:ss', { locale: th }),
          ...order,
        });
      }
      console.log(reports);
      const ws = XLSX.utils.json_to_sheet(reports);
      const wb = { Sheets: { data: ws }, SheetNames: ['data'] };

      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const data = new Blob([excelBuffer], { type: fileType });
      FileSaver.saveAs(data, 'report' + fileExtension);
    } catch (err) {
      console.log(err);
    }
  }

  function exportReport() {
    setLoadingPolicy(true);
    fetchPolicy()
      .then(async (res) => {
        // console.log(res.data);
        const policies = res.data;
        let orders = rows;
        if (!rows.length) {
          await refetch().then((res) => {
            orders = res.data;
          });
        }
        const reports = [];
        const cDate = new Date();
        for (let i = 0; i < policies.length; i++) {
          const policy = policies[i];
          const { pkg } = policy;
          const order = orders.find((x) => x.invoiceId === policy.invoiceId);
          if (!order) {
            continue;
          }
          const amount = PKG?.[pkg] || 990;
          const discount = order?.userDiscount?.[pkg] || 0;
          const parsedDate = parse(policy.policyDate, 'dd/MM/yyyy', cDate, { locale: th });
          const policyDate = format(parsedDate, 'MMM/dd/yyyy');
          delete policy._id;
          delete policy.userId;
          delete order?.coupon;
          // delete policy?.userDisCount;
          reports.push({
            ...policy,
            policyDate,
            amount: amount.toString(),
            discount: discount.toString(),
            netAmount: `${Number(amount) - Number(discount)}`,
            createAt: format(formatIdToDt(order._id), 'dd-MM-yyyy HH:mm:ss', { locale: th }),
            successAt: policy?.successAt ? format(new Date(policy?.successAt), 'dd-MM-yyyy HH:mm:ss', { locale: th }) : '',
            urlPolicyLocal: policy?.urlPolicyLocal ? process.env.REACT_APP_DO_SPACE + policy?.urlPolicyLocal : '',
            qrURL: order?.qrURL || '',
            isPaid: order?.isPaid,
            email: order?.email,
          });
        }
        console.log(reports);
        const ws = XLSX.utils.json_to_sheet(reports, { header });
        const wb = { Sheets: { data: ws }, SheetNames: ['data'] };
        const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        const data = new Blob([excelBuffer], { type: fileType });
        FileSaver.saveAs(data, 'report' + fileExtension);
      })
      .catch(console.error)
      .finally(() => {
        console.log('done');
      });
  }

  function formatIdToDt(_id) {
    const timestamp = _id.toString().substring(0, 8);
    return new Date(parseInt(timestamp, 16) * 1000);
  }

  return (
    <DashboardLayout bgColor="success">
      <DashboardNavbar />
      <ArgonBox py={3}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={12}>
            <ArgonBox>
              <Card>
                <ArgonBox display="flex" justifyContent="space-between" alignItems="flex-start" p={3}>
                  <ArgonBox lineHeight={1}>
                    <ArgonTypography variant="h5" fontWeight="medium">
                      การงานการสั่งซื้อ
                    </ArgonTypography>
                  </ArgonBox>
                  <Stack spacing={1} direction="row">
                    <ArgonButton variant="gradient" color="info" size="small" onClick={exportOrder}>
                      {loadingPolicy ? '...' : 'ส่งออก (รายการสั่งซื้อ)'}
                    </ArgonButton>
                    <ArgonButton variant="gradient" color="info" size="small" onClick={exportReport}>
                      {loadingPolicy ? '...' : 'ส่งออก'}
                    </ArgonButton>
                  </Stack>
                </ArgonBox>
                <ArgonBox ml={3} mb={3}>
                  <Stack spacing={2} direction="row" alignItems="center">
                    <PickerDate label="วันเริ่มต้น" value={start} setValue={setStart} />
                    <PickerDate label="วันสิ้นสุด" value={end} setValue={setEnd} />
                    <ArgonBox pl={3} pt={3}>
                      <FormControlLabel
                        control={<Checkbox checked={isUser} onChange={(e) => setIsUser(e.target.checked)} />}
                        label="สมาชิก"
                      />
                    </ArgonBox>
                    <ArgonBox pt={3}>
                      <FormControlLabel
                        control={<Checkbox checked={isAdmin} onChange={(e) => setIsAdmin(e.target.checked)} />}
                        label="ผู้ดูแลระบบ"
                      />
                    </ArgonBox>
                    <ArgonBox pt={3}>
                      <ArgonButton variant="gradient" color="info" size="small" onClick={handleFetch}>
                        {loading ? '...' : 'ค้นหา'}
                      </ArgonButton>
                    </ArgonBox>
                  </Stack>
                </ArgonBox>
                <ArgonBox px={3}>
                  <DataTable
                    table={{
                      columns: [
                        {
                          Header: 'วันที่/เวลา',
                          Cell: ({ row }) => mongoIdToDt(row.original._id),
                          minWidth: 4,
                          width: 5,
                        },
                        {
                          Header: 'เลขที่อินวอยซ์',
                          accessor: 'invoiceId',
                          minWidth: 4,
                          width: 5,
                          Cell: ({ row }) => <Link to={'/admin/order/' + row.original?.invoiceId}>{row.original?.invoiceId}</Link>,
                        },
                        { Header: 'จำนวน', accessor: 'total', minWidth: 2, width: 2 },
                        { Header: 'มูลค่า', accessor: 'amount', minWidth: 4, width: 4 },
                        {
                          Header: 'สถานะ',
                          accessor: 'status',
                          Cell: ({ row }) => <StatusCell row={row} />,
                        },
                        { Header: 'ผู้ใช้', accessor: 'email' },
                      ],
                      rows,
                    }}
                    entriesPerPage={{
                      defaultValue: 50,
                      entries: [50, 100, 250, 500],
                    }}
                    canSearch
                  />
                </ArgonBox>
              </Card>
            </ArgonBox>
          </Grid>
        </Grid>
      </ArgonBox>
    </DashboardLayout>
  );
};

export default AdminReport;
